import { SceneService } from '../../services/SceneService';
import { PunchListTrackManager } from '../lib/PunchListTrackManager';
import { VideoTranscriptElement } from '../models/elements/VideoTranscriptElement';
import { computeCoverCrop } from '../models/layout/CropTransformUtils';
import { AbstractElementCommand } from './AbstractCommand';
import { CreateScenesForRestoredRegionCommand } from './CreateScenesForRestoredRegionCommand';
import { TimelineMode } from './TimelineMode';

import type {
  FaceDetection,
  ISceneFramingService,
} from '../../services/framing/SmartFramingService';
import type { TrimEndInfo } from '../../services/SceneService';
import type { Element } from '../models/Element';
import type { NaturalDimensions } from '../types/aspectRatio';
import type { SceneDetection } from '../types/sceneDetection';
import type { VideoEditor } from '../VideoEditor';

/**
 * Command to adjust the end of an element (both extending and trimming)
 * Handles both shrinking and extending the duration by changing the element's end time
 *
 * This command replaces the functionality of both the former TrimElementEndCommand
 * and ExtendElementCommand, unifying them into a single implementation that handles
 * both cases:
 * - Extending mode: When new end time > current end time
 * - Trimming mode: When new end time < current end time
 *
 * The command detects automatically which behavior to use based on the comparison
 * between the current element end time and the requested new end time.
 *
 * Features:
 * - Frame snapping of the end time based on editor framerate
 * - Full ripple mode support to push or pull subsequent elements
 * - Video-specific property handling (trim_duration)
 * - State capture for undo/redo
 *
 * Uses direct object references
 */
export class TrimElementEndCommand extends AbstractElementCommand {
  private newEndTime: number;
  private snapToFrame: boolean;

  getName(): string {
    return 'TrimElementEndCommand';
  }
  private originalState: Record<string, any>;
  private originalEndTime: number;
  private sceneService: SceneService;
  private trimEndInfo: TrimEndInfo | null = null;
  private readonly sceneDetections: SceneDetection[] | undefined;
  private readonly faceDetections: FaceDetection[] | undefined;
  private readonly framingService: ISceneFramingService | undefined;
  private readonly naturalMediaDimensions:
    | Record<string, NaturalDimensions>
    | undefined;
  private createScenesSubCommand: CreateScenesForRestoredRegionCommand | null =
    null;
  private isExtending: boolean = false;

  /**
   * Creates a new TrimElementEndCommand
   * @param editor The video editor instance
   * @param element The element to trim
   * @param newEndTime The new end time for the element (in seconds)
   * @param snapToFrame Whether to snap the end time to frame boundaries (default: true)
   * @param sceneDetections Optional scene detections for creating proper scenes when extending
   * @param naturalMediaDimensions Optional natural dimensions for computing crop
   * @param faceDetections Optional face detections for smart framing
   * @param framingService Optional framing service for calculating per-scene crops
   */
  constructor(
    editor: VideoEditor,
    element: Element,
    newEndTime: number,
    snapToFrame: boolean = true,
    sceneDetections?: SceneDetection[],
    naturalMediaDimensions?: Record<string, NaturalDimensions>,
    faceDetections?: FaceDetection[],
    framingService?: ISceneFramingService,
  ) {
    super(editor, element);

    // Apply frame snapping to the end time if enabled
    this.newEndTime = snapToFrame
      ? this.snapTimeToFrame(newEndTime)
      : newEndTime;
    this.snapToFrame = snapToFrame;
    this.sceneDetections = sceneDetections;
    this.naturalMediaDimensions = naturalMediaDimensions;
    this.faceDetections = faceDetections;
    this.framingService = framingService;

    // Capture full element state for undo
    this.originalState = this.captureElementState(this.sourceElement);
    this.originalEndTime = this.sourceElement.end_time;

    // Initialize scene service
    this.sceneService = new SceneService();

    // Capture scene adjustment info BEFORE modification (only for transcript elements)
    if (this.isTranscriptElement()) {
      this.captureTrimEndInfo();
    }

    // Store affected elements for undo in ripple mode
    if (this.getTimelineMode() === TimelineMode.RIPPLE) {
      this.storeAffectedElements({});
    }
  }

  execute(): boolean {
    const isTranscriptElem = this.isTranscriptElement();
    const result = this.performTrimOperation();

    if (result) {
      // Adjust scenes AFTER element modification (only for transcript elements)
      if (isTranscriptElem && this.trimEndInfo) {
        this.sceneService.adjustScenesAfterTrimEnd(
          this.editor.timeline,
          this.trimEndInfo,
        );

        // Create scenes for the extended portion if extending
        if (this.isExtending) {
          this.createScenesForExtendedRegion();
        }
      }

      // Recalculate punch list position before global reconciliation so the
      // updated timing is committed ahead of reconcilePunchList.
      const trackManager = this.editor.getTrackManager();
      const isPunchListElement = trackManager.isPunchListElement(
        this.sourceElement.id,
      );
      if (!isTranscriptElem && isPunchListElement) {
        const punchListTrackManager = new PunchListTrackManager(this.editor);
        punchListTrackManager.recalculatePunchListPosition([
          this.sourceElement.id,
        ]);
      }

      this.recalculateTimeline();
    }

    return result;
  }

  /**
   * Performs the trim operation using the appropriate track manager
   * @returns true if the operation was successful
   */
  private performTrimOperation(): boolean {
    if (this.isTranscriptElement()) {
      return this.trimTranscriptElement();
    } else {
      return this.trimRegularElement();
    }
  }

  /**
   * Checks if the source element is a VideoTranscriptElement
   */
  private isTranscriptElement(): boolean {
    return this.sourceElement instanceof VideoTranscriptElement;
  }

  /**
   * Trims a VideoTranscriptElement using TranscriptTrackManager
   */
  private trimTranscriptElement(): boolean {
    const transcriptTrackManager = this.editor.getTranscriptTrackManager();
    // Use the active TimelineMode for both trims and extensions. The
    // underlying TrackManager.trimElementEnd handles ripple-extend natively
    // (positive timeDelta shifts subsequent elements forward) — same
    // mechanism the transcript restore commands rely on.
    const clipResult = transcriptTrackManager.trimElementEnd(
      this.sourceElement as VideoTranscriptElement,
      this.newEndTime,
      this.getTimelineMode(),
      this.snapToFrame,
    );
    return clipResult !== null;
  }

  /**
   * Trims a regular element using TrackManager
   */
  private trimRegularElement(): boolean {
    const trackManager = this.editor.getTrackManager();
    return trackManager.trimElementEnd(
      this.sourceElement,
      this.newEndTime,
      TimelineMode.DEFAULT,
      // this.getTimelineMode(), always default for non-transcript elements
      this.snapToFrame,
    );
  }

  undo(): boolean {
    // Undo scene creation sub-command first (reverse order)
    if (this.createScenesSubCommand) {
      this.createScenesSubCommand.undo();
    }

    // Restore the complete original element state
    this.restoreElementState(this.sourceElement, this.originalState);

    // Restore affected elements if in ripple mode
    this.restoreAffectedElements();

    // Recalculate timeline duration
    this.recalculateTimeline();

    return true;
  }

  getDescription(): string {
    // Change description based on whether extending or trimming
    const currentEndTime =
      this.sourceElement.time +
      (typeof this.sourceElement.duration === 'number'
        ? this.sourceElement.duration
        : 0);

    if (this.newEndTime > currentEndTime) {
      return `Extend element ${this.sourceElement.id} to ${this.newEndTime}s`;
    }
    return `Trim end of element ${this.sourceElement.id} to ${this.newEndTime}s`;
  }

  /**
   * Captures trim end information for scene adjustment
   * Must be called BEFORE element is modified
   * Assumes element is VideoTranscriptElement (checked by caller)
   */
  private captureTrimEndInfo(): void {
    if (this.sourceElement instanceof VideoTranscriptElement === false) {
      return;
    }

    const transcriptElement = this.sourceElement as VideoTranscriptElement;
    const originalMediaStart = transcriptElement.getMediaStartTime();
    const originalMediaEnd = transcriptElement.getMediaEndTime();

    // Calculate new media end time
    const currentDuration =
      typeof this.sourceElement.duration === 'number'
        ? this.sourceElement.duration
        : 0;
    const newDuration = this.newEndTime - this.sourceElement.time;
    const durationChange = newDuration - currentDuration;
    const newMediaEnd = originalMediaEnd + durationChange;

    // Track if this is an extend operation (for scene creation)
    this.isExtending = newMediaEnd > originalMediaEnd;

    this.trimEndInfo = {
      elementId: this.sourceElement.id,
      originalMediaStart,
      originalMediaEnd,
      newMediaEnd,
    };
  }

  /**
   * Creates scenes for the extended (restored) region when scene detections are available.
   * Called when the element end is moved later (extending).
   */
  private createScenesForExtendedRegion(): void {
    if (
      !this.sceneDetections ||
      this.sceneDetections.length === 0 ||
      !this.trimEndInfo
    ) {
      return;
    }

    const transcriptTrack = this.editor.getVideoTranscriptTrack();
    if (!transcriptTrack) {
      return;
    }

    const { originalMediaEnd, newMediaEnd } = this.trimEndInfo;

    // Only create scenes if extending (newMediaEnd > originalMediaEnd)
    if (newMediaEnd <= originalMediaEnd) {
      return;
    }

    // Compute crop for the element
    const crop = this.computeCoverCropForElement();

    // Create sub-command (don't create its own undo entry)
    this.createScenesSubCommand = new CreateScenesForRestoredRegionCommand(
      this.editor.timeline,
      this.sceneDetections,
      {
        elements: [this.sourceElement],
        mediaStartTime: originalMediaEnd,
        mediaEndTime: newMediaEnd,
      },
      transcriptTrack.id,
      false, // Don't create undo entry - parent command handles undo
      crop ?? undefined,
      this.framingService,
      this.faceDetections,
    );

    // Execute directly (not through executeCommand)
    this.createScenesSubCommand.execute();
  }

  /**
   * Computes a cover crop for the source element.
   * Returns a centered crop that fills the canvas while maintaining aspect ratio.
   */
  private computeCoverCropForElement(): {
    x: number;
    y: number;
    w: number;
    h: number;
  } | null {
    if (!this.naturalMediaDimensions) {
      return null;
    }

    const element = this.sourceElement;
    if (!(element instanceof VideoTranscriptElement)) {
      return null;
    }

    const sourceUrl = element.source;
    const naturalDimensions = this.naturalMediaDimensions[sourceUrl];

    if (!naturalDimensions) {
      return null;
    }

    return computeCoverCrop(
      this.editor.timeline.width,
      this.editor.timeline.height,
      naturalDimensions.width,
      naturalDimensions.height,
    );
  }
}
